/**
 * @providesModule @api/GetLeagueSets
 */

import RESTStore from 'RESTStore';
import {NativeModules} from 'react-native';

import {
  League,
  LeagueType,
  MOCK_FAIL_RESPONSE,
  MOCK_RESPONSE_DELAY,
  USE_MOCK,
  generateMockLeagueFromType,
  mockFailResponse,
} from './LeagueInfo';

type GetLeagueSetsVariables = {
  /**
   * ID of the desired league set
   */
  league_set_configuration_id: string;
  concluded?: boolean;
};
export type GetLeagueSetsResult = {
  /**
   * The list of leagues within the league set
   */
  leagues: League[];
};

export const queryKeys = {
  getLeagueSet: 'getLeagueSet',
} as const;

/**
 * The get call to the Back-End to get the list of league set which the given league is part of.
 * @param variable {GetLeagueSetsVariables} The variable with the given league set id
 * @see https://skillzsdk.docs.apiary.io/#reference/leagues/league-set/get-league-set-leagues
 * @returns A promise of the league set leagues list which the given league set is part of
 */
export const getLeagueSets = (variable: GetLeagueSetsVariables) => {
  return new Promise(
    (resolve: (leagues: GetLeagueSetsResult) => void, reject: (error: Error) => void) => {
      if (USE_MOCK) {
        setTimeout(
          () =>
            MOCK_FAIL_RESPONSE
              ? reject(mockFailResponse)
              : variable.league_set_configuration_id.search('LEVEL') !== -1
                ? resolve(mockGetLevelLeagueSetResult)
                : resolve(mockGetLeagueSetsResult),
          MOCK_RESPONSE_DELAY
        );
      } else {
        if (!variable.league_set_configuration_id) {
          reject(new Error('No league_set_configuration_id provided'));
          return;
        }
        const params = 'concluded' in variable ? {concluded: variable.concluded} : null;
        RESTStore.getServerResponse(
          'engagement/league-sets/' + variable.league_set_configuration_id,
          params as Record<string, string> | null,
          NativeModules.SKZAPIManager.OnError.failSilently,
          (response: GetLeagueSetsResult, error: Error | null) => {
            if (error) {
              reject(error);
            } else {
              resolve(response);
            }
          }
        );
      }
    }
  );
};

export const mockGetLeagueSetsResult: GetLeagueSetsResult = {
  leagues: [generateMockLeagueFromType(LeagueType.Z)],
};

export const mockGetLevelLeagueSetResult: GetLeagueSetsResult = {
  leagues: [
    generateMockLeagueFromType(LeagueType.LEVEL, {
      league_id: 'L1',
      description:
        'Compete against players in this League! Earn 100 medals to be promoted to next League for even better prizes',
    }),
    generateMockLeagueFromType(LeagueType.LEVEL, {
      league_id: 'L2',
      description:
        'Compete against players in this League! Earn 1000 medals to be promoted to next League for even better prizes',
    }),
    generateMockLeagueFromType(LeagueType.LEVEL, {
      league_id: 'L3',
      description:
        'Compete against players in this League! Earn 10000 medals to be promoted to next League for even better prizes',
    }),
  ],
};

export const mockGetMultipleLeagueSetResult = (league_id: string) => {
  const result: GetLeagueSetsResult = {
    leagues: [
      generateMockLeagueFromType(LeagueType.TICKETZ, {
        league_id: league_id,
      }),
      generateMockLeagueFromType(LeagueType.TICKETZ, {
        league_id: 'L2',
      }),
      generateMockLeagueFromType(LeagueType.TICKETZ, {
        league_id: 'L3',
      }),
    ],
  };
  return result;
};
